import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useLiveQuery } from "dexie-react-hooks";
import { getConfig, getCriancaAtiva } from "../db/queries";
import { Logo } from "../ui/Logo";
import { IconeWhatsApp } from "../ui/IconeWhatsApp";
import { PacienteAvatar } from "../ui/PacienteAvatar";

/**
 * Tela inicial (raiz) — escolhe o módulo. O Degrau é o app guarda-chuva (ambos os
 * módulos usam uma escada de degraus de aproximação); cada módulo é uma jornada
 * própria, mas compartilham o mesmo paciente e o mesmo aparelho (local-first).
 */

// número fixo de suporte/feedback (WhatsApp, com DDI 55 + DDD 11)
const FEEDBACK_WHATSAPP = "5511966437887";

const MODULOS_FEEDBACK: { v: string; r: string }[] = [
  { v: "pitada", r: "Pitada — alimentar" },
  { v: "compasso", r: "Compasso — música" },
  { v: "elo", r: "Elo — integração" },
  { v: "gingado", r: "Gingado — psicomotricidade" },
];

export function ModuloHome() {
  const nav = useNavigate();
  const crianca = useLiveQuery(getCriancaAtiva);
  const cfg = useLiveQuery(getConfig);
  const [mostrarFeedback, setMostrarFeedback] = useState(false);
  const [moduloFb, setModuloFb] = useState("");
  const [mensagemFb, setMensagemFb] = useState("");

  // primeiro nome de quem está cadastrado (responsável), pra assinar a mensagem
  const primeiroNome = crianca?.responsavel_nome?.trim().split(/\s+/)[0] ?? "";

  function fecharFeedback() {
    setMostrarFeedback(false);
    setModuloFb("");
    setMensagemFb("");
  }

  function enviarFeedback() {
    if (!moduloFb) return; // seleção obrigatória
    const label = MODULOS_FEEDBACK.find((m) => m.v === moduloFb)?.r ?? moduloFb;
    const linhas = [`*Degrau · feedback*`, `Módulo: ${label}`];
    if (primeiroNome) linhas.push(`De: ${primeiroNome}`);
    linhas.push("", mensagemFb.trim());
    const url = `https://wa.me/${FEEDBACK_WHATSAPP}?text=${encodeURIComponent(linhas.join("\n"))}`;
    window.open(url, "_blank", "noopener");
    fecharFeedback();
  }

  return (
    <main className="tela">
      <div className="row-entre">
        <div className="row" style={{ gap: "var(--s3)" }}>
          <Logo size={48} />
          <div>
            <h1 className="tela-titulo" style={{ marginBottom: 2 }}>
              Degrau
            </h1>
            <p className="tela-sub" style={{ margin: 0 }}>
              aplicativo para apoio ao desenvolvimento da criança
            </p>
          </div>
        </div>
        <Link
          to="/paciente"
          className="perfil-atalho"
          aria-label={crianca ? `perfil de ${crianca.apelido}` : "meu perfil"}
        >
          <PacienteAvatar crianca={crianca} size={40} />
          <span className="perfil-txt">{crianca?.apelido ?? "Meu Perfil"}</span>
        </Link>
      </div>

      {crianca && (
        <p className="meta" style={{ color: "var(--text-muted)", margin: "var(--s4) 0 var(--s2)" }}>
          paciente ativo: <strong>{crianca.apelido}</strong>
        </p>
      )}

      <section className="modulos">
        {/* Painel do profissional — exclusivo do profissional (flag). Banner no topo,
            largura de 2 cards. No modo família (flag off) nem aparece. */}
        {cfg?.perfil_profissional && (
          <button className="modulo-card mod-painel" onClick={() => nav("/painel")}>
            <span className="modulo-emoji" aria-hidden>
              🩺
            </span>
            <span className="mod-painel-txt">
              <span className="modulo-nome">Painel do profissional</span>
              <span className="modulo-sub">visão geral — pacientes, módulos e agenda</span>
            </span>
          </button>
        )}

        <button className="modulo-card mod-degrau" onClick={() => nav("/degrau")}>
          <span className="modulo-emoji" aria-hidden>
            🍓
          </span>
          <span className="modulo-nome">Pitada</span>
          <span className="modulo-sub">terapia alimentar — seletividade</span>
        </button>

        <button className="modulo-card mod-compasso" onClick={() => nav("/compasso")}>
          <span className="modulo-emoji" aria-hidden>
            🎵
          </span>
          <span className="modulo-nome">Compasso</span>
          <span className="modulo-sub">musicoterapia — engajamento & regulação</span>
        </button>

        <button className="modulo-card mod-gingado" onClick={() => nav("/gingado")}>
          <span className="modulo-emoji" aria-hidden>
            🤸
          </span>
          <span className="modulo-nome">Gingado</span>
          <span className="modulo-sub">psicomotricidade — participação & regulação</span>
        </button>

        <button className="modulo-card mod-elo" onClick={() => nav("/elo")}>
          <span className="modulo-emoji" aria-hidden>
            🔗
          </span>
          <span className="modulo-nome">Elo</span>
          <span className="modulo-sub">integração — fono · TO · psico · nutri</span>
        </button>

        {/* Agenda da clínica: camada opcional. Só aparece quando ativada — caso
            contrário, o início fica idêntico (nada muda se não for "contratada"). */}
        {cfg?.agenda_ativa && (
          <button className="modulo-card mod-compasso" onClick={() => nav("/agenda")}>
            <span className="modulo-emoji" aria-hidden>
              🗓️
            </span>
            <span className="modulo-nome">Agenda</span>
            <span className="modulo-sub">gestão de clínica — consultas & calendário</span>
          </button>
        )}
      </section>

      {!crianca && (
        <button
          className="card linha-alimento"
          style={{ width: "100%", marginTop: "var(--s5)" }}
          onClick={() => nav("/paciente")}
        >
          <div className="foto" aria-hidden>
            👤
          </div>
          <div className="crescer">
            <div className="nome">complete o cadastro do paciente</div>
            <div className="meta">vale para os dois módulos</div>
          </div>
          <span aria-hidden>→</span>
        </button>
      )}

      {/* feedback — abre um seletor de módulo (obrigatório) e envia pelo WhatsApp */}
      <section className="secao" style={{ marginTop: "var(--s6)" }}>
        {!mostrarFeedback ? (
          <button className="btn btn-bloco" onClick={() => setMostrarFeedback(true)}>
            💬 enviar feedback
          </button>
        ) : (
          <div className="card">
            <div className="campo">
              <label>
                sobre qual módulo? <span style={{ color: "var(--bad, #bd6b5a)" }}>*</span>
              </label>
              <select
                value={moduloFb}
                onChange={(e) => setModuloFb(e.target.value)}
                aria-required="true"
              >
                <option value="">selecione o módulo…</option>
                {MODULOS_FEEDBACK.map((m) => (
                  <option key={m.v} value={m.v}>
                    {m.r}
                  </option>
                ))}
              </select>
            </div>

            <div className="campo">
              <label>sua mensagem</label>
              <textarea
                value={mensagemFb}
                onChange={(e) => setMensagemFb(e.target.value)}
                placeholder="conte o que achou, o que faltou, uma ideia…"
                rows={4}
              />
            </div>

            <div className="row" style={{ gap: "var(--s2)" }}>
              <button className="btn crescer" onClick={fecharFeedback}>
                cancelar
              </button>
              <button
                className="btn btn-primario crescer"
                disabled={!moduloFb}
                style={
                  moduloFb
                    ? { background: "#25d366", borderColor: "#25d366", color: "#fff" }
                    : undefined
                }
                onClick={enviarFeedback}
              >
                <IconeWhatsApp /> abrir WhatsApp
              </button>
            </div>
            <p className="meta" style={{ color: "var(--text-suave)", margin: "var(--s2) 0 0" }}>
              escolha o módulo para continuar. abre o WhatsApp com a mensagem pronta.
            </p>
          </div>
        )}
      </section>

      <p style={{ textAlign: "center", marginTop: "var(--s4)" }}>
        <Link to="/nuvem" style={{ color: "var(--text-muted)", fontSize: ".88rem", textDecoration: "none", fontWeight: 600 }}>
          ☁️ Modo profissional (nuvem) →
        </Link>
      </p>

      <p className="rel-rodape" style={{ marginTop: "var(--s5)", border: "none" }}>
        tudo fica neste aparelho, sem conta. cada módulo organiza dados — não substitui o
        profissional.
      </p>
    </main>
  );
}
